/**
 * Переліки наказів на відпустку і розрахунків відпускних (Block 2.1).
 * Виробничий календар — окрема сторінка pages/refs/ProductionCalendarPage (ч.1118).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  vacationOrder,
  vacationCalculation,
  type VacationOrder,
  type VacationCalculation,
} from '@/api/vacation';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function VacationOrdersPage() {
  const navigate = useNavigate();
  // 2026-08-23 (Норма #29): серверна пагінація/сортування, ПІБ з сервера,
  // фільтр орг рядком (слідує за шапкою), колонки «Вкладення»/«Підпис».
  const [orgId, setOrgId] = useOrgFilter();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])), [orgList.data]);
  const { datasource, total } = useInfiniteGridDatasource<VacationOrder>(
    (limit, offset, sort) => vacationOrder.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId],
    { attachmentsType: 'doc.vacation_orders' },
  );
  const sc = useSignControlInfinite<VacationOrder>('doc.vacation_orders', orgId);
  const cols = useMemo<ColDef<VacationOrder>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: VacationOrder }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      field: 'document_date', headerName: 'Дата', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 180,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    { field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    {
      field: 'vacation_type', headerName: 'Тип', width: 130,
      valueFormatter: (p) => p.data ? ({
        ANNUAL_BASIC: 'Основна', ANNUAL_ADDITIONAL: 'Додаткова',
        SOCIAL: 'Соціальна', UNPAID: 'Без ЗП', CREATIVE: 'Творча',
      } as Record<string, string>)[p.value] ?? p.value : '',
    },
    { field: 'vacation_start_date', headerName: 'З', width: 110 },
    { field: 'vacation_end_date', headerName: 'По', width: 110 },
    { field: 'days_count', headerName: 'Днів', width: 80, cellStyle: { textAlign: 'right' } },
    {
      field: 'is_paid', headerName: 'Оплата', width: 90, cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => p.data ? (p.value ? '✓' : '—') : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);
  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Накази відпустки<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/hr/vacation-orders/new')}>
          + Новий наказ
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', margin: '0 0 0.6rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
      </div>
      <InfiniteDocGrid<VacationOrder>
        deleteRow={{ remove: (id) => vacationOrder.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="vacation-orders"
        onRowDoubleClicked={(r) => navigate(`/hr/vacation-orders/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}


export function VacationCalculationsPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])), [orgList.data]);
  const { datasource, total } = useInfiniteGridDatasource<VacationCalculation>(
    (limit, offset, sort) => vacationCalculation.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId],
  );
  const money = (p: { value?: string | number | null }) => p.value != null ? fmt2(p.value) : '';
  const cols = useMemo<ColDef<VacationCalculation>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: VacationCalculation }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 140 },
    {
      field: 'document_date', headerName: 'Дата', width: 150,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 180,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    { field: 'employee_name', headerName: 'Працівник', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    { field: 'vacation_start_date', headerName: 'Відпустка з', width: 130 },
    { field: 'days_count', headerName: 'Днів', width: 80, cellStyle: { textAlign: 'right' } },
    { field: 'vacation_gross_amount', headerName: 'Нараховано', width: 140, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'net_amount', headerName: 'До виплати', width: 140, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    {
      field: 'posted', headerName: 'Проведено', width: 110, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);
  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Розрахунки відпускних<TitleTotal total={total} /></h2>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', margin: '0 0 0.6rem' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
      </div>
      <InfiniteDocGrid<VacationCalculation>
        deleteRow={{ remove: (id) => vacationCalculation.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="vacation-calculations"
        onRowDoubleClicked={(r) => navigate(`/hr/vacation-calculations/${r.id}`)}
        getRowStyle={() => undefined}
      />
    </div>
  );
}
